import { IUser } from "services/UserContext";

/**
 * Salesforce Embedded Messaging ("Support Chat") configuration.
 *
 * Ported from HAR's Member Only Area footers
 * (application/views/memberonlyarea/footer_mop.php and footer_platinum.php),
 * which run the same deployment. The values below must stay in sync with those
 * footers -- both point at the same Service Cloud org and messaging channel, so
 * an agent who starts a conversation here lands in the same queue.
 *
 * Note: the deployment only accepts origins that are allowlisted in Salesforce
 * Setup (Embedded Service Deployments -> CSP Trusted Sites). If an AI Search
 * host is missing there, the bootstrap loads but never fires
 * `onEmbeddedMessagingReady`, and the button simply never appears.
 */
export const SUPPORT_CHAT = {
  orgId: "00D1U00000100Mu",
  deploymentName: "HAR_Messaging_Web_har_com",
  deploymentUrl: "https://har123.my.site.com/ESWHARMessagingWebhar1716911584831",
  scrt2Url: "https://har123.my.salesforce-scrt.com",
} as const;

export const SUPPORT_CHAT_BOOTSTRAP_SRC = `${SUPPORT_CHAT.deploymentUrl}/assets/js/bootstrap.min.js`;

/**
 * The messaging window is hosted on this origin and posts lifecycle events to
 * the parent page. Messages from anywhere else are ignored. Derived rather than
 * written out again so a change of deployment host cannot silently leave the
 * postMessage handler listening to the wrong origin.
 */
export const SUPPORT_CHAT_ORIGIN = new URL(SUPPORT_CHAT.deploymentUrl).origin;

/**
 * Support chat is for HAR members reaching member services, so it is limited to
 * agents: `> 1` rather than `=== 2` so every agent role id qualifies, matching
 * the check in Filters.tsx. Guests are 0 and consumers are 1. It is also
 * suppressed in embed mode, where the app runs inside an iframe on har.com and
 * the host page has its own support entry point.
 *
 * Shared so the launcher and the copy that points at it cannot disagree.
 */
export function isSupportChatAvailable(user: IUser | undefined, embed: boolean) {
  return (user?.role_id ?? 0) > 1 && !embed;
}

/**
 * Whether this page is allowed to embed the Salesforce chat window at all.
 *
 * Both chat frames answer with
 *   Content-Security-Policy: frame-ancestors har.com *.har.com har123--c.vf.force.com
 * and a CSP host-source that names neither scheme nor port matches only https
 * on the default port. So the widget can be embedded from https://har.com and
 * its subdomains on 443, and nowhere else -- which rules out
 * http://aisearch-local.har.com:3100 and any other local or preview host.
 *
 * This has to be decided up front because the failure is invisible at runtime:
 * the browser refuses the frame, the bootstrap never fires
 * `onEmbeddedMessagingReady`, and the launcher would simply never appear. We
 * would rather show the button and explain than silently drop it.
 */
export function isSupportChatEmbeddable(
  location: Pick<Location, "protocol" | "hostname" | "port"> = window.location
) {
  const { protocol, hostname, port } = location;
  return (
    protocol === "https:" &&
    (hostname === "har.com" || hostname.endsWith(".har.com")) &&
    // Empty means the scheme's default, i.e. 443 for https.
    (port === "" || port === "443")
  );
}

export interface IPrechatField {
  value: string;
  isEditableByEndUser: boolean;
}

/**
 * The departments offered by the deployment's required "Choose a Department"
 * prechat field (`Request_Type`).
 *
 * These are the `choiceListValueName`s from the live embedded-service-config,
 * not the display labels. Naming a department here that Salesforce does not
 * define would fail the bootstrap's field validation, and a rejected field is
 * dropped with only a console log -- hence the union rather than a bare string.
 */
export type SupportChatDepartment = "Member_Services" | "MLS_Support";

/**
 * `maxLength` for the prechat fields callers can set, taken from the same form
 * definition. Salesforce rejects an over-long value outright -- it drops the
 * field rather than truncating it -- so values are cut to fit instead.
 */
export const SUPPORT_CHAT_MAX_LENGTH = {
  subject: 80,
  department: 20,
} as const;

/** Prechat values the caller can supply to route or label the conversation. */
export interface ISupportChatContext {
  /** Prefills "Subject". Trimmed and truncated to fit the form. */
  subject?: string;
  /** Preselects "Choose a Department". */
  department?: SupportChatDepartment;
}

/**
 * Normalize a value for a visible prechat field.
 *
 * The bootstrap validates every value and drops the entire field when one
 * fails, logging to the console and doing nothing else. Two of its rules apply
 * here: the field's maxLength, and a filter that rejects any value containing
 * `javascript:` or `<script>`.
 *
 * Both are resolved up front so no field can disappear on us. An over-long
 * value is truncated to fit; a value the filter would reject is discarded. In
 * either case the result is a plain string, empty when there is nothing usable
 * to send, and the caller decides what empty means -- `buildPrechatFields`
 * still sends an empty subject, but omits the department field entirely.
 */
export function toPrechatValue(
  value: string | undefined,
  maxLength: number
): string {
  const trimmed = value?.trim();
  if (!trimmed || /javascript:|<script>/i.test(trimmed)) return "";
  return trimmed.slice(0, maxLength);
}

/**
 * Minimal typing for the pieces of the Salesforce bootstrap we actually call.
 * The real global has a much wider surface; declaring only what we use keeps
 * the compiler honest about the calls in SupportChat.tsx.
 */
export interface IEmbeddedServiceBootstrap {
  settings: {
    language?: string;
    /** "x,y" offsets for the native launcher, which we park offscreen. */
    chatButtonPosition?: string;
    hideChatButtonOnLoad?: boolean;
    devMode?: boolean;
  };
  init(
    orgId: string,
    deploymentName: string,
    deploymentUrl: string,
    options: { scrt2URL: string }
  ): void;
  prechatAPI: {
    setVisiblePrechatFields(fields: Record<string, IPrechatField>): void;
  };
  utilAPI: {
    launchChat(): Promise<unknown>;
  };
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: IEmbeddedServiceBootstrap;
  }
}
